import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { faInfoSquare, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import {ImageMetadata} from "@webmate-web-core/shared/api/rest/image.restapi";
import {HighlightedElement} from "@webmate-web-core/shared/components/state/highlightedElement.stateinfo";
import {ImageService} from "@webmate-web-core/shared/services/image.service";
import {StateInfoItem} from "@webmate-web-core/shared/components/state/stateinfoitemrendererfactory";

@Component({
    selector: 'wm-a11y-observation-list',
    templateUrl: './a11y-observation-list.component.html',
    styleUrls: ['./a11y-observation-list.component.less']
})
export class A11yObservationListComponent implements OnChanges {

    @Input() observations: any[] = [];
    @Input() reportData: any;
    @Input() artifact: any;
    @Input() observationType: 'violation' | 'pass';

    activeTarget: any;
    activeObservation: any;
    activeTargetFailureSummary: string;

    activeElement?: any;
    activeElementStateInfos?: StateInfoItem[];
    activeElementScreenshotMeta?: ImageMetadata;

    constructor(private imageService: ImageService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.observations) {
            this.activeTarget = undefined;
            this.activeObservation = undefined;
        }
    }

    showObservation(observation: any, node: any, target: any) {
        this.activeTarget = node;
        this.activeObservation = observation;

        // Find the element descriptor
        this.activeElement = this.reportData?.elements?.find(
            (element: any) => element.mainSelector?.selector === target
        );

        // Calculate screenshot positioning if available
        if (this.activeElement?.fullBoundingBox && this.activeElement.elementScreenshot) {
            const { xRel, yRel, widthRel, heightRel } = this.activeElement.fullBoundingBox;

            this.imageService.getImageMetadata(this.activeElement.elementScreenshot).subscribe(meta => {
                this.activeElementScreenshotMeta = meta || undefined;

                const { 
                    xRel: scrXRel = 0, 
                    yRel: scrYRel = 0, 
                    widthRel: scrWidthRel = 1, 
                    heightRel: scrHeightRel = 1 
                } = meta?.position || {};

                // Calculate relative position within screenshot
                const xRelInScr = xRel - scrXRel;
                const yRelInScr = (yRel - scrYRel) * (1 / scrHeightRel);
                const heightRelInScr = heightRel * (1 / scrHeightRel);
                const widthRelInScr = widthRel * (1 / scrWidthRel);

                this.activeElementStateInfos = [
                    new HighlightedElement(
                        { x: xRelInScr, y: yRelInScr, width: widthRelInScr, height: heightRelInScr },
                        false,
                        'red',
                        true,
                        true
                    )
                ];
            });
        }

        this.activeTargetFailureSummary = this.createFailureSummary(node);
    }

    createFailureSummary(node: any): string {
        if (node.failureSummary) {
            return this.formatFailureSummary(node.failureSummary);
        }

        // Fallback to constructing from messages
        const anyMessages = node.any?.map((n: any) => n.message).filter(Boolean) || [];
        const allMessages = node.all?.map((n: any) => n.message).filter(Boolean) || [];
        const noneMessages = node.none?.map((n: any) => n.message).filter(Boolean) || [];

        let failureSummary = '';

        if (anyMessages.length > 0) {
            failureSummary += 'Fix any of the following:<br><ul class="repair-list">';
            anyMessages.forEach((message) => {
                failureSummary += `<li>${message}</li>`;
            });
            failureSummary += '</ul>';
        }

        if (allMessages.length > 0) {
            failureSummary += 'Fix all of the following:<br><ul class="repair-list">';
            allMessages.forEach((message) => {
                failureSummary += `<li>${message}</li>`;
            });
            failureSummary += '</ul>';
        }

        if (noneMessages.length > 0) {
            failureSummary += 'Fix none of the following:<br><ul class="repair-list">';
            noneMessages.forEach((message) => {
                failureSummary += `<li>${message}</li>`;
            });
            failureSummary += '</ul>';
        }

        return failureSummary.replace(/\n/g, '<br>');
    }

    formatFailureSummary(summary: string): string {
        const lines = summary.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        
        if (lines.length === 0) {
            return summary;
        }

        let result = '';
        let inList = false;

        const headerPrefixes = [
            "fix any of the following",
            "fix all of the following",
            "korrigiere mindestens einen der folgenden punkte",
            "korrigiere alle der folgenden punkte",
            "corriger tous les éléments suivants",
            "corriger l'un des éléments suivants"
        ];

        for (const line of lines) {
            const lower = line.toLowerCase();

            if (headerPrefixes.some(prefix => lower.startsWith(prefix))) {
                if (inList) {
                    result += "</ul>";
                    inList = false;
                }
                result += `<p>${line}</p>`;
            } else {
                if (!inList) {
                    result += `<ul class="repair-list">`;
                    inList = true;
                }
                result += `<li>${line}</li>`;
            }
        }

        if (inList) {
            result += "</ul>";
        }

        return result;
    }

    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faInfoSquare = faInfoSquare;
}
